import { useQuery } from "@apollo/client";
// import { ProjectLoading } from "@govlaunch/project";
import { Box } from "@govlaunch/web";
import { useRouter } from "next/router";
import React, { useRef } from "react";
import { useSelfie } from "~/components/auth/Auth";
import UpgradeToMakersProBanner from "~/components/vendor/profile/UpgradeToMakersProBanner";
import { findLanguageByLocale } from "~/intl/locales";
import Redirect from "~/lib/route/Redirect";
import { useIntlLocale } from "~/lib/withIntl";
import stringOrFirst from "~/src/utils/stringOrFirst";
import TranslateTo from "~/src/components/translation/TranslateTo";
import ProjectBySlugQuery from "~/src/components/project/graphql/ProjectBySlugQuery";
import {
  IProjectBySlugQuery,
  IProjectBySlugQueryVariables,
} from "~/src/components/project/graphql/__generated__/ProjectBySlugQuery.generated";
import { ProjectBackLink } from "~/src/components/project/ProjectBackLink";
import ProjectHead from "~/src/components/project/ProjectHead";
import ProjectHeaderNavigation from "~/src/components/project/ProjectHeaderNavigation";
import ProjectIsPendingVerification from "~/src/components/project/ProjectIsPendingVerification";
import ProjectNotFound from "~/src/components/project/ProjectNotFound";
import ProjectWithActions from "~/src/components/project/ProjectWithActions";

export default function ProjectBySlugPage() {
  const router = useRouter();
  const viewer = useSelfie();
  const token = useRef<string | null>(stringOrFirst(router.query.token));
  const projectSlug = stringOrFirst(router.query.projectSlug);

  const lang = stringOrFirst(router.query.lang);
  const langExists = typeof lang !== "undefined" && lang.length > 0 && lang !== "en-US";
  const { locale } = useIntlLocale();
  const targetLanguage = lang || locale;
  const showTranslateButton = langExists || locale !== "en-US";

  const { data, loading, refetch } = useQuery<IProjectBySlugQuery, IProjectBySlugQueryVariables>(ProjectBySlugQuery, {
    variables: {
      slug: projectSlug,
      token: token.current || null,
      language: findLanguageByLocale(lang),
    },
    fetchPolicy: "cache-and-network",
  });

  const translateToButton = () => {
    return (
      <TranslateTo
        langExists={langExists}
        showTranslateButton={showTranslateButton}
        refetch={() =>
          refetch({
            slug: projectSlug,
            token: token.current || null,
            language: findLanguageByLocale(targetLanguage),
          })
        }
        refetchOriginal={() =>
          refetch({
            slug: projectSlug,
            token: token.current || null,
            language: null,
          })
        }
      />
    );
  };

  // if (loading) {
  //   return <ProjectLoading />;
  // }

  if (!loading && !data?.projectBySlug) {
    return viewer ? <ProjectNotFound /> : <Redirect to={`/signin?redirectTo=/projects/${projectSlug}`} />;
  }

  if (!loading && (!data?.projectBySlug?.viewerCanRead || (token.current && !viewer))) {
    return <Redirect to={`/signin?redirectTo=/projects/${projectSlug}`} />;
  }

  if (!loading && viewer && viewer.__typename === "VendorUser" && !data?.projectBySlug?.viewerCanView) {
    return <UpgradeToMakersProBanner />;
  }

  const isPendingVerification =
    data?.projectBySlug &&
    data.projectBySlug.__typename === "VendorProject" &&
    data.projectBySlug.locations.length === 0 &&
    data.projectBySlug.locationsPendingVerification.length > 0;

  return (
    <Box maxW={800} margin="0 auto" p={[1, 1, 0]}>
      {data?.projectBySlug && <ProjectHead project={data.projectBySlug} />}

      <ProjectHeaderNavigation />

      {isPendingVerification && (
        <Box position="relative" mb={4}>
          <ProjectIsPendingVerification />
        </Box>
      )}

      <Box p={[2, 8]} borderRadius="md" backgroundColor="white" border="solid 1px" borderColor="gray.100" mb={6}>
        {isPendingVerification && <Box height={[12, 12, 0, 0]} />}
        <ProjectWithActions
          loading={loading}
          project={data?.projectBySlug || null}
          onDeleteProject={() => {
            router.push("/projects");
          }}
          translationButton={translateToButton}
        />
      </Box>

      {data?.projectBySlug && (
        <Box ml={[8, 8, 8, 0]}>
          <ProjectBackLink project={data.projectBySlug} />
        </Box>
      )}
    </Box>
  );
}
